/**
 * Help Center Button - The ? icon that opens the Help Center
 */

import React, { useState, useMemo, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { HelpCenter, HelpArticleData } from './HelpCenter';
import { helpContentRegistry } from './helpContentRegistry';

export interface HelpCenterButtonProps {
  className?: string;
  customArticles?: HelpArticleData[];
}

// Help content is registered by plugins via their onInit hook
// No need to load it here - it's already in the registry

export function HelpCenterButton({ className = '', customArticles = [] }: HelpCenterButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [registeredArticles, setRegisteredArticles] = useState<HelpArticleData[]>([]);

  // Subscribe to registry updates - this will update when plugins register their articles
  useEffect(() => {
    // Get initial articles
    const initialArticles = helpContentRegistry.getAllArticles();
    setRegisteredArticles(initialArticles);
    
    if (import.meta.env?.DEV && initialArticles.length > 0) {
      console.log(`[HelpCenterButton] Loaded ${initialArticles.length} registered help articles from plugins`);
    }
    
    // Subscribe to updates
    const unsubscribe = helpContentRegistry.subscribe((articles) => {
      setRegisteredArticles(articles);
      if (import.meta.env?.DEV) {
        console.log(`[HelpCenterButton] Registry updated: ${articles.length} total articles`);
      }
    });
    
    return unsubscribe;
  }, []);

  // Collect plugin articles from all sources
  // Priority: Plugin articles first, then default articles
  const allCustomArticles = useMemo(() => {
    // Plugin articles from registry first (priority), then any other custom articles
    const combined = [...registeredArticles, ...customArticles];
    
    // Debug logging in development
    if (import.meta.env?.DEV) {
      console.log(`[HelpCenterButton] Total custom articles: ${combined.length} (${registeredArticles.length} from registry, ${customArticles.length} from props)`);
    }
    
    return combined;
  }, [registeredArticles, customArticles]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`
          p-2 rounded-lg 
          text-slate-600 dark:text-slate-300
          hover:bg-slate-100 dark:hover:bg-slate-700 
          hover:text-blue-600 dark:hover:text-blue-400
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${className}
        `}
        title="Help Center"
        aria-label="Open Help Center"
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      <HelpCenter 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)}
        customArticles={allCustomArticles}
      />
    </>
  );
}





